/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/no-non-null-assertion */
import { Injector, ɵisInjectable } from '@angular/core';
import { ActivatedRouteSnapshot, PRIMARY_OUTLET, Router, UrlSegment } from '@angular/router';

import { NavigationInfo } from '@frontend/vanilla/core';

export function getToken<T = any>(token: any, snapshot: ActivatedRouteSnapshot, moduleInjector: Injector): T {
    return getTokenWithInjector<T>(token, snapshot, moduleInjector).token;
}

/**
 * From '@angular/router/fesm2022/router.mjs
 * @param as
 * @param bs
 * @returns
 */
function equalPath(as: UrlSegment[], bs: UrlSegment[]) {
    if (as.length !== bs.length) {
        return false;
    }
    return as.every((a, i) => a.path === bs[i].path);
}

/**
 * From '@angular/router/fesm2022/router.mjs
 */
function shallowEqual(a: Record<symbol | string, unknown>, b: Record<symbol | string, unknown>) {
    // While `undefined` should never be possible, it would sometimes be the case in IE 11
    // and pre-chromium Edge. The check below accounts for this edge case.
    const k1 = a ? getDataKeys(a) : undefined;
    const k2 = b ? getDataKeys(b) : undefined;
    // eslint-disable-next-line eqeqeq
    if (!k1 || !k2 || k1.length != k2.length) {
        return false;
    }
    let key: string | symbol;
    for (let i = 0; i < k1.length; i++) {
        key = k1[i];
        if (!equalArraysOrString(a[key], b[key])) {
            return false;
        }
    }
    return true;
}

function getDataKeys(obj: object) {
    return [...Object.keys(obj), ...Object.getOwnPropertySymbols(obj)];
}

function equalArraysOrString(a: unknown, b: unknown) {
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }

        const aSorted = [...a].sort();
        const bSorted = [...b].sort();

        return aSorted.every((val, index) => bSorted[index] === val);
    } else {
        return a === b;
    }
}

function getClosestRouteInjector(snapshot?: any) {
    if (!snapshot) {
        return null;
    }
    // If the current route has its own injector, which is created from the static providers on the
    // route itself, we should use that. Otherwise, we start at the parent since we do not want to
    // include the lazy loaded injector from this route.
    if (snapshot.routeConfig?._injector) {
        return snapshot.routeConfig._injector;
    }
    for (let s = snapshot.parent; s; s = s.parent) {
        const route = s.routeConfig;
        // Note that the order here is important. `_loadedInjector` stored on the route with
        // `loadChildren: () => NgModule` so it applies to child routes with priority. The `_injector`
        // is created from the static providers on that parent route, so it applies to the children as
        // well, but only if there is no lazy loaded NgModuleRef injector.
        if (route?._loadedInjector) {
            return route._loadedInjector;
        }
        if (route?._injector) {
            return route._injector;
        }
    }
    return null;
}

function getTokenOrFunctionIdentity(tokenOrFunction: any, injector: Injector) {
    const NOT_FOUND = Symbol();
    const result = injector.get(tokenOrFunction, NOT_FOUND);
    if (result === NOT_FOUND) {
        if (typeof tokenOrFunction === 'function' && !ɵisInjectable(tokenOrFunction)) {
            // We think the token is just a function so return it as-is
            return tokenOrFunction;
        } else {
            // This will throw the not found error
            return injector.get(tokenOrFunction);
        }
    }
    return result;
}

export function getTokenWithInjector<T = any>(
    token: any,
    snapshot: ActivatedRouteSnapshot,
    moduleInjector: Injector,
): { token: T; injector: Injector } {
    const injector = getClosestRouteInjector(snapshot) || moduleInjector;

    return { token: getTokenOrFunctionIdentity(token, injector), injector };
}

export function paramsOrQueryParamsChange(from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot): boolean {
    const popupChanged = from.queryParamMap.has('popup') !== to.queryParamMap.has('popup') && to.queryParamMap.has('popup');

    if (popupChanged || shouldSkipGuardsAndResolvers(to)) {
        return false;
    }

    // same as: https://github.com/angular/angular/blob/master/packages/router/src/utils/preactivation.ts#L164
    return !equalParamsAndUrlSegments(from, to) || !shallowEqual(from.queryParams, to.queryParams);
}

export function equalParamsAndUrlSegments(f: ActivatedRouteSnapshot, t: ActivatedRouteSnapshot): boolean {
    const equalUrlParams = shallowEqual(f.params, t.params) && equalSegments(f.url, t.url);
    const parentsMismatch = !f.parent !== !t.parent;

    return equalUrlParams && !parentsMismatch && (!f.parent || equalParamsAndUrlSegments(f.parent, t.parent!));
}

function equalSegments(aSegments: UrlSegment[], bSegments: UrlSegment[]): boolean {
    return equalPath(aSegments, bSegments) && aSegments.every((a, i) => shallowEqual(a.parameters, bSegments[i].parameters));
}

function shouldSkipGuardsAndResolvers(to: ActivatedRouteSnapshot): boolean {
    const router: Router | undefined = getClosestRouteInjector(to)?.get(Router);
    const info = router?.getCurrentNavigation()?.extras.info;

    return hasSkipInfo(info) && info.skipPrimaryOutletGuardsAndResolvers && to.outlet === PRIMARY_OUTLET;
}

function hasSkipInfo(info: unknown): info is Required<Pick<NavigationInfo, 'skipPrimaryOutletGuardsAndResolvers'>> {
    return Boolean(info && (info as NavigationInfo).skipPrimaryOutletGuardsAndResolvers !== undefined);
}
